import { Flag } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { cn } from "@/lib/cn";
import { useReportStore, type ReportTarget } from "@/services/reportStore";
import { useShowcase } from "@/services/showcase";

/**
 * "Report": the DSA Art. 16 notice-and-action entry point on a venue or an
 * offer (docs/features/reports.md). Opens the account sidebar's report box
 * pre-filled with what is reported; guests get the sign-in / email path there.
 * A person reviews every report (terms §6). Not in the server-free demo.
 */
export function ReportLink({
  target,
  label,
  onRequest,
  className,
}: {
  target: ReportTarget;
  /** Read by screen readers ("Report POMET"). */
  label: string;
  /** Runs first, e.g. to close the popup the link sits in. */
  onRequest?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const showcase = useShowcase();
  const requestReport = useReportStore((s) => s.requestReport);
  if (showcase) return null;

  return (
    <button
      type="button"
      onClick={() => {
        onRequest?.();
        requestReport(target);
      }}
      aria-label={t("REPORT_ARIA", { what: label })}
      className={cn(
        "inline-flex items-center gap-1 text-2xs text-dim light:text-zinc-600 transition hover:text-zinc-300 light:hover:text-zinc-700",
        className,
      )}
    >
      <Flag className="h-3 w-3" aria-hidden />
      {t("REPORT_LINK")}
    </button>
  );
}
